import { supabase } from "@/lib/supabase";
import type { EmployeeRow } from "@/types/database";
import { fetchAllPagesBounded } from "@/lib/api/boundedListFetch";

export interface ListEmployeesFilters {
  /** Manager scoping: include only employees whose job_category_id is one of these (OR-match). */
  jobCategoryIds?: string[];
  /** Optional location filter (OR-match). */
  locationIds?: string[];
  status?: EmployeeRow["status"];
}

export type EmployeeRecord = EmployeeRow;

/**
 * List employees for an org.
 * Internally pages PostgREST (F2L-27) so a single request is never unbounded,
 * while still returning the full permitted roster for MAPS/cohort callers.
 * If the hard ceiling is hit, throws so UI cannot silently miss people.
 */
export async function listEmployees(
  orgId: string,
  filters?: ListEmployeesFilters
): Promise<EmployeeRow[]> {
  if (import.meta.env.DEV) {
    console.log("[Fit2Lift API] Query org filter: employees", { orgId, filters: filters ?? null });
  }

  const { rows, truncated } = await fetchAllPagesBounded<EmployeeRow>(async (from, to) => {
    let q = supabase
      .from("employees")
      .select("*")
      .eq("org_id", orgId)
      .order("last_name", { ascending: true })
      .order("first_name", { ascending: true })
      .range(from, to);

    if (filters?.status) {
      q = q.eq("status", filters.status);
    }
    if (filters?.jobCategoryIds && filters.jobCategoryIds.length > 0) {
      q = q.in("job_category_id", filters.jobCategoryIds);
    }
    if (filters?.locationIds && filters.locationIds.length > 0) {
      q = q.in("location_id", filters.locationIds);
    }

    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as EmployeeRow[];
  });

  if (truncated) {
    throw new Error(
      "Employee roster exceeds the maximum loadable size. Narrow filters or contact support.",
    );
  }

  return rows.sort((a, b) => {
    const ln = a.last_name.localeCompare(b.last_name);
    return ln !== 0 ? ln : a.first_name.localeCompare(b.first_name);
  });
}

/** Roster row linked to a portal login (`employees.user_id` → auth user). */
export async function getEmployeeLinkedToUser(orgId: string, authUserId: string): Promise<EmployeeRow | null> {
  const { data, error } = await supabase
    .from("employees")
    .select("*")
    .eq("org_id", orgId)
    .eq("user_id", authUserId)
    .maybeSingle();
  if (error && error.code !== "PGRST116") throw error;
  if (!data) return null;
  return data as EmployeeRow;
}

export async function getEmployee(id: string): Promise<EmployeeRow | null> {
  const { data, error } = await supabase
    .from("employees")
    .select("*")
    .eq("id", id)
    .single();
  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data as EmployeeRow;
}

export async function createEmployee(
  input: Record<string, unknown>
): Promise<EmployeeRow> {
  const { data, error } = await supabase
    .from("employees")
    .insert(input as never)
    .select()
    .single();
  if (error) throw error;
  return data as EmployeeRow;
}

export async function updateEmployee(
  id: string,
  input: Record<string, unknown>
): Promise<EmployeeRow> {
  const { data, error } = await supabase
    .from("employees")
    .update(input as never)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as EmployeeRow;
}

export async function deleteEmployee(id: string): Promise<void> {
  const { error } = await supabase.from("employees").delete().eq("id", id);
  if (error) throw error;
}
